

























































































































import {
	computed,
	defineComponent,
	PropType,
	Ref,
	ref,
	nextTick,
} from '@vue/composition-api';
import { unique } from '@front-app/util/array';
import { DropdownPosition } from '@front-app/components/dropdowns/dropdownPosition';
import { Size } from '@front-app/components/constants';
import SimpleDropdown from '@front-app/components/dropdowns/SimpleDropdown.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CategoryButton from '../buttons/concrete/CategoryButton.vue';

const SELECTION_ITEM_IGNORED_KEYS = [
	'Tab',
	'Shift',
	'Meta',
	'ArrowUp',
	'ArrowDown',
	'ArrowLeft',
	'ArrowRight',
	'Enter',
	'Escape',
];

export default defineComponent({
	components: {
		FontAwesomeIcon,
		CtaButton,
		SimpleDropdown,
		CategoryButton,
	},
	props: {
		canAddNewCategories: Boolean,
		id: {
			type: String,
			required: true,
		},
		categories: {
			type: Array as PropType<string[]>,
			required: true,
		},
		selectedCategories: {
			type: Array as PropType<string[]>,
			required: true,
		},
		selectedNewCategories: {
			type: Array as PropType<string[]>,
			required: false,
			default: () => [],
		},
		position: {
			type: Number as PropType<DropdownPosition>,
			default: DropdownPosition.BOTTOM_RIGHT,
		},
		hasError: Boolean,
		translateCategories: Boolean,
		numMaxSelections: {
			type: Number,
			default: Infinity,
		},
	},
	setup(props, ctx) {
		const isMenuOpen = ref(false);
		const userText = ref('');
		const uniqueCategories = computed(() => unique(props.categories));
		const allSelectedCategoriesForComparison = computed(() => props.selectedCategories
			.concat(props.selectedNewCategories)
			.map(normalizeTextForCompare));
		const existingCategoriesForComparison = computed(() => uniqueCategories.value.map(normalizeTextForCompare));
		const categoryIsSelected = (cat: string): boolean => {
			const inner = cat.trim().toLocaleLowerCase();
			return allSelectedCategoriesForComparison.value.includes(inner);
		};
		const categoryExistsOrIsSelected = (cat: string): boolean => {
			const inner = cat.trim().toLocaleLowerCase();
			return existingCategoriesForComparison.value.includes(inner)
				|| categoryIsSelected(cat);
		};
		const visibleCategories = computed(() => uniqueCategories.value
			.filter((cat) => {
				const translatedCat = props.translateCategories ? (ctx.root.$t(cat) as unknown as string) : cat;
				return translatedCat.toLocaleLowerCase().includes(userText.value.toLocaleLowerCase());
			}));
		const selectFirst = () => {
			const userTextVal = userText.value.trim();
			if (userTextVal && props.canAddNewCategories && !categoryExistsOrIsSelected(userTextVal)) {
				ctx.emit('select-new', userTextVal);
				userText.value = '';
			} else {
				const selectableCategories = visibleCategories.value.filter((cat) => !categoryIsSelected(normalizeTextForCompare(cat)));
				if (selectableCategories.length > 0) {
					ctx.emit('select', selectableCategories[0]);
					userText.value = '';
				}
			}
		};
		const deleteLastTagIfEmpty = () => {
			if (userText.value.length < 1) {
				if (props.selectedCategories.length > 0) {
					ctx.emit('remove', props.selectedCategories[props.selectedCategories.length - 1]);
				} else if (props.selectedNewCategories.length > 0) {
					ctx.emit('remove-new', props.selectedNewCategories[props.selectedNewCategories.length - 1]);
				}
			}
		};
		const categoryTextField = ref(null) as Ref<null | HTMLInputElement>;
		const focusInput = () => {
			const el = categoryTextField.value;
			if (el) {
				el.focus();
			}
		};
		const resetTextField = () => {
			userText.value = '';
			focusInput();
		};

		const onButtonKeydown = (e: KeyboardEvent) => {
			if (!SELECTION_ITEM_IGNORED_KEYS.includes(e.key)) {
				focusInput();
				if (e.key !== 'Escape') {
					isMenuOpen.value = true;
				}
			}
		};

		const closeOnKeyboard = async () => {
			await nextTick();
			isMenuOpen.value = false;
			userText.value = '';
		};
		const closeOnClick = async () => {
			await nextTick();
			closeOnKeyboard();
		};

		const atMaxSelections = computed(() => props.numMaxSelections <= (props.selectedCategories.length + props.selectedNewCategories.length));

		// Button focus management
		const dropdownButtonWrapper: Ref<null | HTMLElement> = ref(null);
		const findButtonElements = () => {
			if (dropdownButtonWrapper.value === null) {
				return [];
			}
			return Array.from(dropdownButtonWrapper.value.querySelectorAll('button'));
		};
		const focusedIdx = ref(0);
		const focusPreviousCategory = () => {
			const buttons = findButtonElements();
			focusedIdx.value = ((focusedIdx.value - 1) + buttons.length) % buttons.length;
			buttons[focusedIdx.value]?.focus();
		};
		const focusNextCategory = () => {
			const buttons = findButtonElements();
			focusedIdx.value = (focusedIdx.value + 1) % buttons.length;
			buttons[focusedIdx.value]?.focus();
		};
		const focusFirstCategory = () => {
			const buttons = findButtonElements();
			focusedIdx.value = 0;
			buttons[0]?.focus();
		};
		// END: Button focus management

		return {
			userText,
			isMenuOpen,
			categoryIsSelected,
			categoryExistsOrIsSelected,
			visibleCategories,
			selectFirst,
			deleteLastTagIfEmpty,
			categoryTextField,
			resetTextField,
			onButtonKeydown,
			closeOnClick,
			closeOnKeyboard,
			atMaxSelections,
			// Focus looping
			dropdownButtonWrapper,
			focusPreviousCategory,
			focusNextCategory,
			focusFirstCategory,
			// Util
			Size,
			ButtonPalettes,
		};
	},
});

function normalizeTextForCompare(str: string): string {
	return str.trim().toLocaleLowerCase();
}

